import { useEffect, useId, useRef, useState } from "react";
import { type CitySuggestion, getCitySuggestions } from "../api";

const DEBOUNCE_MS = 280;
const MIN_CHARS = 2;

export function CityAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing a city…",
  testid = "city-autocomplete",
}: {
  value: string;
  onChange: (city: string) => void;
  onSelect?: (s: CitySuggestion) => void;
  placeholder?: string;
  testid?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CitySuggestion[]>([]);
  const [highlight, setHighlight] = useState(0);
  // Track the last *user-typed* query so we only fetch on real user input,
  // not on programmatic selection.
  const lastQueryRef = useRef<string>(value);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    const q = lastQueryRef.current.trim();
    if (q.length < MIN_CHARS) {
      setItems([]);
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { results } = await getCitySuggestions(q, ctrl.signal);
        setItems(results);
        setHighlight(0);
      } catch (e) {
        if ((e as { name?: string }).name !== "AbortError") {
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [value, open]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function commit(s: CitySuggestion) {
    onChange(s.label);
    lastQueryRef.current = s.label;
    onSelect?.(s);
    setOpen(false);
    setItems([]);
    inputRef.current?.blur();
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || items.length === 0) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = items[highlight];
      if (s) commit(s);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        className="input"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          lastQueryRef.current = v;
          onChange(v);
          setOpen(true);
        }}
        onFocus={() => value.length >= MIN_CHARS && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        data-testid={testid}
      />
      {open && (loading || items.length > 0) && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-auto rounded-md border border-border bg-surface shadow-md"
          data-testid={`${testid}-list`}
        >
          {loading && items.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted">Searching…</li>
          )}
          {items.map((s, i) => (
            <li
              key={s.id}
              role="option"
              aria-selected={i === highlight}
              className={`px-3 py-2 text-sm cursor-pointer ${
                i === highlight ? "bg-paper text-ink" : "text-ink/90 hover:bg-paper"
              }`}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus
                commit(s);
              }}
              data-testid={`${testid}-option-${i}`}
            >
              <span className="font-medium">{s.city}</span>
              {(s.region || s.country) && (
                <span className="text-muted">
                  {" "}
                  · {[s.region, s.country].filter(Boolean).join(", ")}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
